import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { BatchCardPreview } from 'src/components/BatchCardPreview';
import Loader from 'src/components/loader';
import { api } from 'src/utils/api';
import {
  API_RESOURCES,
  MATERIAL_CONTAINER_STATUSES,
  PAGINATION_IGNORE_DEFAULT_LIMIT,
} from 'src/utils/constants';
import { getUuid } from 'src/utils/url';

export const BatchCardPreviewContainer = ({ batchUuid, isExpanded }) => {
  const [isResourceFetching, setIsResourceFetching] = useState(false);
  const [batchState, setBatchState] = useState({
    batch: null,
    allContainers: [],
    containersFetching: true,
    containersFetchError: null,
    loadedPrinter: null,
    loadedPostProcessor: null,
    subLocation: null,
  });

  const fetchMaterialBatch = async () => {
    try {
      setIsResourceFetching(true);

      // 1. Fetch the main batch data
      const batchData = await api.get(`${API_RESOURCES.MATERIAL_BATCH}/${batchUuid}/`).json();

      // Prepare the object we'll merge into our state
      let newState = {
        batch: batchData,
      };

      // 2. If batchData exists, fetch other related resources
      if (batchData) {
        const { EMPTY, ...containerStatusesExceptEmpty } = MATERIAL_CONTAINER_STATUSES;

        // Prepare parallel fetch calls
        const containerFetchPromise = api
          .get(`${API_RESOURCES.MATERIAL_CONTAINER}/`, {
            searchParams: {
              'filter[current_batch]': batchData.uri,
              'filter[status]': Object.values(containerStatusesExceptEmpty).join(','),
              'page[limit]': PAGINATION_IGNORE_DEFAULT_LIMIT,
            },
          })
          .json();

        const subLocationFetchPromise = api
          .get(`${API_RESOURCES.SUB_LOCATION}/${getUuid(batchData.sub_location)}/`)
          .json();

        // Only fetch printer data if at_machine is present
        const printerFetchPromise = batchData.at_machine
          ? api.get(`${API_RESOURCES.PRINTER}/${getUuid(batchData.at_machine)}/`).json()
          : Promise.resolve(null);

        const postProcessorFetchPromise = batchData.post_processor
          ? api.get(`${API_RESOURCES.POST_PROCESSOR}/${getUuid(batchData.post_processor)}/`).json()
          : Promise.resolve(null);

        // Wait for all requests in parallel
        const [containerFetchResult, subLocationData, loadedPrinterData, loadedPostProcessorData] =
          await Promise.all([
            containerFetchPromise,
            subLocationFetchPromise,
            printerFetchPromise,
            postProcessorFetchPromise,
          ]);

        // Set the derived data in newState
        newState = {
          ...newState,
          allContainers: containerFetchResult.resources,
          containersFetching: false,
          subLocation: subLocationData,
        };

        if (loadedPrinterData) {
          newState.loadedPrinter = loadedPrinterData;
        }

        if (loadedPostProcessorData) {
          newState.loadedPostProcessor = loadedPostProcessorData;
        }
      }

      setIsResourceFetching(false);

      // 4. Perform one state update
      setBatchState(prevState => ({
        ...prevState,
        ...newState,
      }));
    } catch (e) {
      // If any fetch fails, handle errors and end loading states
      setIsResourceFetching(false);
      setBatchState(prevState => ({
        ...prevState,
        containersFetching: false,
        containersFetchError: e,
      }));
    }
  };

  useEffect(() => {
    fetchMaterialBatch();
  }, [batchUuid]);

  return isResourceFetching ? (
    <Loader isMedium showText={false} className='mr3' />
  ) : (
    <BatchCardPreview
      shouldShowBatchLink
      batch={batchState.batch}
      allContainers={batchState.allContainers}
      containersFetching={batchState.containersFetching}
      containersFetchError={batchState.containersFetchError}
      subLocation={batchState.subLocation?.name}
      loadedPrinter={batchState.loadedPrinter}
      loadedPostProcessor={batchState.loadedPostProcessor}
      isExpanded={isExpanded}
    />
  );
};

BatchCardPreviewContainer.propTypes = {
  batchUuid: PropTypes.string.isRequired,
  isExpanded: PropTypes.bool,
};
